import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

import { AppLayout } from '@/components/AppLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { useAuthor } from '@/hooks/useAuthor';
import { useNostrRepos } from '@/hooks/useNostrRepos';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsFollowing, useFollowMutation } from '@/hooks/useFollow';
import { useUserProjectComments, useProjectByCoordinate } from '@/hooks/useUserProjectComments';
import { NoteContent } from '@/components/NoteContent';
import { genUserName } from '@/lib/genUserName';

import { Globe2, Link as LinkIcon, GitBranch, User, Copy, MessageSquare, UserPlus, UserMinus, Loader2 } from 'lucide-react';
import type { NostrMetadata, NostrEvent } from '@nostrify/nostrify';

import ExploreRepositoryCard from './ProfileViewRepositoryCard';

interface ProfileViewProps {
  pubkey: string;
}

function ProfileHeaderSkeleton() {
  return (
    <div className="flex flex-col items-center text-center gap-4 mb-10">
      <Skeleton className="h-20 w-20 rounded-full" />
      <div className="space-y-2 max-w-xs">
        <Skeleton className="h-5 w-40 mx-auto" />
        <Skeleton className="h-4 w-52 mx-auto" />
      </div>
    </div>
  );
}

function buildDisplayName(pubkey: string, metadata?: NostrMetadata): string {
  if (!metadata) return genUserName(pubkey);
  return metadata.display_name || metadata.name || genUserName(pubkey);
}

interface CommentCardProps {
  comment: NostrEvent;
  projectCoordinate: string;
  projectDTag: string;
  projectAuthorPubkey: string;
}

function CommentCard({ comment, projectDTag, projectAuthorPubkey }: CommentCardProps) {
  const { t } = useTranslation();
  const { data: project, isLoading } = useProjectByCoordinate(`30617:${projectAuthorPubkey}:${projectDTag}`);

  const npub = useMemo(() => nip19.npubEncode(projectAuthorPubkey), [projectAuthorPubkey]);
  const projectName = useMemo(() => {
    if (!project) return projectDTag;
    const nameTag = project.tags.find((tag) => tag[0] === 'name');
    const dTag = project.tags.find((tag) => tag[0] === 'd');
    return nameTag?.[1] || dTag?.[1] || projectDTag;
  }, [project, projectDTag]);

  const timeAgo = formatDistanceToNow(new Date(comment.created_at * 1000), { addSuffix: true });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="bg-card/50 hover:bg-card/80 transition-colors">
        <CardContent className="p-4 space-y-3">
          {/* Project reference */}
          <div className="flex items-center justify-between">
            <Link
              to={`/${npub}/${projectDTag}`}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              <GitBranch className="h-3.5 w-3.5" />
              {isLoading ? <Skeleton className="h-4 w-24" /> : projectName}
            </Link>
            <span className="text-xs text-muted-foreground">{timeAgo}</span>
          </div>

          {/* Comment content */}
          <div className="text-sm text-foreground/90">
            <NoteContent event={comment} className="text-sm line-clamp-4" />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function FollowButton({ pubkey }: { pubkey: string }) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const isFollowing = useIsFollowing(pubkey);
  const { mutate: followMutation, isPending } = useFollowMutation();

  // Don't show follow button for own profile
  if (!user || user.pubkey === pubkey) {
    return null;
  }

  const handleClick = () => {
    followMutation({
      pubkey,
      action: isFollowing ? 'unfollow' : 'follow',
    });
  };

  return (
    <Button
      size="sm"
      variant={isFollowing ? 'outline' : 'default'}
      className="rounded-full text-xs px-4"
      onClick={handleClick}
      disabled={isPending}
    >
      {isPending ? (
        <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
      ) : isFollowing ? (
        <UserMinus className="h-3.5 w-3.5 mr-2" />
      ) : (
        <UserPlus className="h-3.5 w-3.5 mr-2" />
      )}
      {isFollowing ? t('unfollow', 'Unfollow') : t('follow', 'Follow')}
    </Button>
  );
}

export function ProfileView({ pubkey }: ProfileViewProps) {
  const { t } = useTranslation();
  const { data: authorData, isLoading: isLoadingAuthor } = useAuthor(pubkey);
  const { data: repos, isLoading: isLoadingRepos } = useNostrRepos({ authorPubkey: pubkey, limit: 200 });
  const { data: projectComments, isLoading: isLoadingComments } = useUserProjectComments(pubkey, 100);

  const metadata = authorData?.metadata as NostrMetadata | undefined;
  const displayName = buildDisplayName(pubkey, metadata);
  const about = metadata?.about;
  const npub = useMemo(() => nip19.npubEncode(pubkey), [pubkey]);

  const projectCount = repos?.length ?? 0;

  const profileUrl = `https://afterhours.diy/${npub}`;
  const ogImage = metadata?.picture || 'https://afterhours.diy/afterhours-og.png';
  const seoDescription = metadata?.about || `${displayName} - Developer profile on AfterHours`;

  useSeoMeta({
    title: `${displayName} – AfterHours Profile`,
    description: seoDescription,
    ogTitle: `${displayName} – AfterHours`,
    ogDescription: seoDescription,
    ogImage: ogImage,
    ogUrl: profileUrl,
    ogType: 'profile',
    twitterCard: 'summary',
    twitterTitle: `${displayName} – AfterHours`,
    twitterDescription: seoDescription,
    twitterImage: ogImage,
    link: [
      { rel: 'canonical', href: profileUrl },
    ],
  });

  // JSON-LD Structured Data
  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: displayName,
    identifier: npub,
    url: profileUrl,
    ...(metadata?.picture && { image: metadata.picture }),
    ...(metadata?.about && { description: metadata.about }),
    ...(metadata?.nip05 && { email: metadata.nip05 }),
    ...(metadata?.website && { sameAs: metadata.website }),
    ...(projectCount > 0 && {
      worksFor: {
        '@type': 'Organization',
        name: 'AfterHours',
        url: 'https://afterhours.diy',
      },
      numberOfProjects: projectCount,
    }),
  }), [displayName, npub, profileUrl, metadata, projectCount]);

  const handleCopyNpub = async () => {
    try {
      await navigator.clipboard.writeText(npub);
    } catch (error) {
      console.error('Failed to copy npub:', error);
    }
  };

  return (
    <AppLayout>
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          {isLoadingAuthor ? (
            <ProfileHeaderSkeleton />
          ) : (
            <div className="flex flex-col items-center text-center gap-5">
              <div className="relative">
                <div className="h-24 w-24 rounded-full bg-muted flex items-center justify-center overflow-hidden border border-border/60 shadow-sm">
                  {metadata?.picture ? (
                    <img
                      src={metadata.picture}
                      alt={displayName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User className="h-10 w-10 text-muted-foreground" />
                  )}
                </div>
              </div>

              <div className="space-y-2 max-w-xl">
                <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
                  {displayName}
                </h1>
                {metadata?.nip05 && (
                  <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                    <Globe2 className="h-3.5 w-3.5" />
                    <span>{metadata.nip05}</span>
                  </div>
                )}
                {about && (
                  <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                    {about}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3">
                <FollowButton pubkey={pubkey} />
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full text-xs px-4"
                  onClick={handleCopyNpub}
                >
                  <Copy className="h-3.5 w-3.5 mr-2" />
                  {t('copyProfileAddress', 'Copy profile address')}
                </Button>
                {metadata?.website && (
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="rounded-full text-xs px-4"
                  >
                    <a href={metadata.website} target="_blank" rel="noreferrer">
                      <LinkIcon className="h-3.5 w-3.5 mr-2" />
                      {t('visitWebsite', 'Visit website')}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </motion.div>

        <Tabs defaultValue="projects" className="w-full">
          <TabsList className="mx-auto mb-8 flex w-full max-w-xs justify-center rounded-full bg-muted/60 p-1">
            <TabsTrigger
              value="projects"
              className="flex-1 rounded-full text-xs md:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm gap-2"
            >
              {t('projectsTab', 'Projects')}
              {!isLoadingRepos && (
                <Badge variant="secondary" className="rounded-full text-[10px] px-1.5 py-0 h-4 min-w-[1.25rem]">
                  {projectCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="comments"
              className="flex-1 rounded-full text-xs md:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              {t('commentsTab', 'Comments')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="mt-0">
            {isLoadingRepos ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/60 p-4">
                    <Skeleton className="h-32 w-full rounded-xl mb-4" />
                    <Skeleton className="h-4 w-2/3 mb-2" />
                    <Skeleton className="h-4 w-full mb-1" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ))}
              </div>
            ) : repos && repos.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {repos.map((repo, index) => (
                  <ExploreRepositoryCard key={repo.id} repo={repo} index={index} />
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl border-2 border-dashed border-border/60 bg-muted/40 py-16 px-6 text-center"
              >
                <GitBranch className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                <p className="font-medium text-muted-foreground mb-1">
                  {t('noProjectsYet', 'No repositories yet')}
                </p>
                <p className="text-sm text-muted-foreground/80 max-w-md mx-auto">
                  {t(
                    'noProjectsOnProfileDescription',
                    'When this user publishes repositories via Nostr git, they will appear here.',
                  )}
                </p>
              </motion.div>
            )}
          </TabsContent>

          <TabsContent value="comments" className="mt-0">
            {isLoadingComments ? (
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Card key={index} className="bg-card/50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                      <Skeleton className="h-16 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : projectComments && projectComments.length > 0 ? (
              <div className="space-y-4">
                {projectComments.map((item) => (
                  <CommentCard
                    key={item.comment.id}
                    comment={item.comment}
                    projectCoordinate={item.projectCoordinate}
                    projectDTag={item.projectDTag}
                    projectAuthorPubkey={item.projectAuthorPubkey}
                  />
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl border-2 border-dashed border-border/60 bg-muted/40 py-16 px-6 text-center"
              >
                <MessageSquare className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                <p className="font-medium text-muted-foreground mb-1">
                  {t('noCommentsYet', 'No comments yet')}
                </p>
                <p className="text-sm text-muted-foreground/80 max-w-md mx-auto">
                  {t(
                    'noCommentsOnProfileDescription',
                    'When this user comments on projects, their comments will appear here.',
                  )}
                </p>
              </motion.div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
